import { IniInfo } from "system/types/interfaces";

export function getRuleSignature(rule: IniInfo): string {
  return [
    rule.isDeleted ? "D" : "N",
    rule.isComment ? "C" : "R",
    (rule.name ?? "").trim(),
    (rule.delimiter ?? "").trim(),
    (rule.value ?? "").trim(),
    (rule.applicability ?? "").trim(),
    (rule.inlineComment ?? "").trim(),
  ].join("::");
}

export function tagOriginal<T extends IniInfo>(rule: T, index: number): T {
  return {
    ...rule,
    _originalSig: getRuleSignature(rule),
    _originalIndex: index,
    _originalFields: {
      name: rule.name,
      value: rule.value,
      delimiter: rule.delimiter,
      inlineComment: rule.inlineComment,
      isComment: rule.isComment,
    },
  };
}

// AB#2881: restore original fields on re-comment so signature matches the load-time one.
export function restoreOriginalCommentFields<T extends IniInfo>(rule: T): T | null {
  const orig = rule._originalFields;
  if (!orig || orig.isComment !== true) return null;
  return {
    ...rule,
    isComment: true,
    name: orig.name,
    value: orig.value,
    delimiter: orig.delimiter,
    inlineComment: orig.inlineComment,
  };
}

// AB#3115: restore original fields on un-comment so signature matches the load-time one
// when the line was originally NOT a comment (mirror of restoreOriginalCommentFields).
// Only restores if the current commented value is exactly what we'd produce by commenting
// the original fields — otherwise the user edited the row before/during the commented state
// and we must let the manual parser preserve those edits.
export function restoreOriginalUncommentFields<T extends IniInfo>(rule: T): T | null {
  const orig = rule._originalFields;
  if (!orig || orig.isComment !== false) return null;

  const expectedFromOrig =
    `${orig.name ?? ""} ${orig.delimiter ?? ""} ${orig.value ?? ""}` +
    (orig.inlineComment ? ` ;${orig.inlineComment}` : "");
  const normalize = (s: string) => s.replace(/\s+/g, " ").trim();
  const currentUncommented = (rule.value ?? "").replace(/^;\s*/, "");
  if (normalize(currentUncommented) !== normalize(expectedFromOrig)) return null;

  return {
    ...rule,
    isComment: false,
    name: orig.name,
    value: orig.value,
    delimiter: orig.delimiter,
    inlineComment: orig.inlineComment,
  };
}

export function isContentDirty(rule: IniInfo): boolean {
  if (rule._originalSig === undefined) return true;
  return getRuleSignature(rule) !== rule._originalSig;
}

export function isReorderDirty(rule: IniInfo, allRows: IniInfo[]): boolean {
  if (rule._originalIndex === undefined) return false;
  const originalRows = allRows.filter((r) => r._originalIndex !== undefined);
  const rankAmongOriginals = originalRows.findIndex((r) => r.id === rule.id);
  return rankAmongOriginals !== rule._originalIndex;
}

export function isRowDirty(rule: IniInfo, allRows: IniInfo[]): boolean {
  return isContentDirty(rule) || isReorderDirty(rule, allRows);
}

export function buildDirtyIdSet(rows: IniInfo[]): Set<string> {
  const set = new Set<string>();
  rows.forEach((r) => {
    if (r.id && isRowDirty(r, rows)) set.add(r.id);
  });
  return set;
}
